import { Component, Host, Prop, h } from '@stencil/core';

/**
 * @description Badge Component
 * @use
 * @since 5.27.0
 * @slot - `default slot`: The content within the badge to be displayed.
 * @version figma-v1
 */

@Component({
  tag: 'hp6-badge',
  styleUrl: 'badge.scss',
  shadow: true,
})
export class Badge {
  /**
   * The aria-label attribute of the container
   */
  @Prop() allyLabel: string;

  /**
   * The badge content. Can be either a number or a short string
   */
  @Prop() badgeContent: string;

  /**
   * If true, a border around the badge will appear
   */
  @Prop() showBorder: boolean;

  /**
   * If true, the badge will be invisible
   */
  @Prop() hideBadge: boolean = false;

  /**
   * If the badge content is a number, the maximum value allowed
   */
  // relect single word prop
  @Prop({ reflect: true }) max: number = 99;

  /**
   * Wrapped shape the badge should overlap
   */
  @Prop() overlapSelection: 'rectangle' | 'circle' = 'rectangle';

  /**
   * Controls whether the badge is hidden when the badgeContent is the number 0
   */
  @Prop() showZero: boolean;

  /**
   * The dot property changes a badge into a small dot. This can be used as a notification that something has changed without displaying the badge content.
   */
  @Prop() variantSelection: 'standard' | 'dot' = 'standard';

  render() {
    const {
      allyLabel,
      badgeContent,
      showBorder,
      hideBadge,
      max,
      overlapSelection,
      showZero,
      variantSelection,
    } = this;

    const invisible
      = hideBadge
        || (badgeContent === '0' && !showZero)
        || (!badgeContent && variantSelection !== 'dot');

    let displayValue = '';

    if (variantSelection !== 'dot') {
      displayValue = parseInt(badgeContent, 10) > max ? `${max}+` : badgeContent;
    }

    return (
      <Host aria-label={allyLabel || displayValue}>
        <div class="badge-container">
          <span class="badge-child">
            <slot />
          </span>
          <span
            class={`badge-content ${showBorder ? 'border' : ''} ${
              invisible ? 'invisible' : ''
            } ${variantSelection === 'standard' ? 'standard' : 'dot'} ${
              overlapSelection === 'rectangle' ? 'rectangle' : 'circle'
            }
          ${showZero ? 'show-zero' : ''}`}
          >
            {displayValue}
          </span>
        </div>
      </Host>
    );
  }
}
